import React, { useCallback, useMemo } from 'react';
import Select, {
  ActionMeta,
  GroupBase,
  Props,
  SelectComponentsConfig,
} from 'react-select';
import classNames from 'classnames';

import { SkipSSR } from 'app/packages/components';

import { IconSize } from '../Icon';
import { selectComponents } from './components';
import Control from './components/Control';
import MenuList from './components/MenuList';
import Option from './components/Option';
import SingleValue from './components/SingleValue';
import { DropdownSize, setCustomStyles } from './constants';
import { getIndicatorSize } from './indicator-size-util';
import useDropdownAPI from './useDropdownAPI';

import styles from './Dropdown.module.scss';

// ---

export { MenuList, Option, Control, SingleValue, DropdownSize };
export type { Props, GroupBase };
export type ChangeHandler<T> = (option: T | null, action?: ActionMeta<T>) => void;
export type ComponentsConfig<T> = SelectComponentsConfig<
  T,
  false,
  GroupBase<T>
>;

declare module 'react-select/dist/declarations/src/Select' {
  export interface Props<
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>
    > {
    disabled?: boolean;
    indicatorSize?: IconSize | number;
    error?: never;
    size?: DropdownSize;
    dynamicOptions?:boolean;
  }
}

// ---

export function Dropdown<
  T,
  IsMulti extends boolean = false,
  Group extends GroupBase<T> = GroupBase<T>
>({
  value,
  className,
  error,
  size = DropdownSize.DEFAULT,
  disabled,
  isSearchable = true,
  components,
  menuPlacement = 'auto',
  maxMenuHeight = 300,
  indicatorSize,
  closeMenuOnScroll = false,
  options,
  dynamicOptions,
  ...props
}: Props<T, IsMulti, Group>): JSX.Element {
  const { setTheme } = useDropdownAPI();

  const dropdownClassnames = classNames(
    styles.dropdown,
    {
      [styles.error]: error,
      [styles.large]: size === DropdownSize.LARGE,
      [styles.medium]: size === DropdownSize.MEDIUM,
      [styles.small]: size === DropdownSize.SMALL,
    },
    className,
  );

  // Не закрываем дропдаун если скролим внутри него
  const closeMenuOnScrollHandler = useCallback((event: Event) => {
    if (!(event.target instanceof HTMLElement)) {
      console.error('event.target isn`t a HTMLElement');
      return false;
    }

    return event.target.closest && !event.target.closest('.dropdown__menu-list');
  }, []);

  const closeMenu = typeof closeMenuOnScroll === 'function'
    ? closeMenuOnScroll
    : (closeMenuOnScroll && closeMenuOnScrollHandler);

  const customStyles = setCustomStyles<T, IsMulti, Group>();

  const memoizedOption = useMemo(() => options, []);
  const selectOptions = dynamicOptions ? options : memoizedOption;

  return (
    <SkipSSR>
      <Select
        data-test="dropdown"
        indicatorSize={getIndicatorSize(indicatorSize, size)}
        components={{ ...selectComponents<T, IsMulti, Group>(), ...components }}
        className={dropdownClassnames}
        classNamePrefix="dropdown"
        styles={customStyles}
        isSearchable={isSearchable}
        isDisabled={disabled}
        openMenuOnClick={!disabled}
        maxMenuHeight={maxMenuHeight}
        menuPlacement={menuPlacement}
        closeMenuOnScroll={closeMenu}
        theme={setTheme}
        captureMenuScroll={false}
        value={value}
        options={selectOptions}
        {...props} />
    </SkipSSR>
  );
}
